// CharacterAttributesGUI.ts
import CharacterStorage from "../Storage/CharacterStorage";
import CharacterAttributes from "../Character/CharacterAttributes";

type AttributeKey = "str" | "vit" | "dex" | "int";

const UI = {
  fontFamily: "monospace",
  fontSize: "28px",
  color: "#000000",
  rowGap: 8,
  lane: {
    // label | value | buttons
    labelWidth: 180,
    valueWidth: 80, // enough for 5 digits comfortably with monospace
    xPadding: 16,
    betweenButtons: 12,
  },
  buttons: {
    // texture keys
    plus: "plus_button",
    minus: "minus_button",
    scale: 1, // tweak if your button art is large
  },
};

class PlusMinusRow {
  public container: Phaser.GameObjects.Container;
  public labelText: Phaser.GameObjects.Text;
  public valueText: Phaser.GameObjects.Text;
  public plusBtn: Phaser.GameObjects.Image;
  public minusBtn: Phaser.GameObjects.Image;

  private _name: string;
  private _addedCount = 0; // how many points were added via UI (caps how much we can remove)
  private _availableRef = () => 0; // getter from parent
  private _onPlus: () => void;
  private _onMinus: () => void;

  constructor(
    scene: Phaser.Scene,
    name: string,
    getAvailablePoints: () => number,
    onPlus: () => void,
    onMinus: () => void
  ) {
    this._name = name;
    this._onPlus = onPlus;
    this._onMinus = onMinus;
    this._availableRef = getAvailablePoints;

    // --- layout primitives
    this.labelText = scene.add.text(0, 0, name, {
      fontFamily: UI.fontFamily,
      fontSize: UI.fontSize,
      color: UI.color,
    });
    this.labelText
      .setOrigin(0, 0.5)
      .setFixedSize(UI.lane.labelWidth, this.labelText.height);

    this.valueText = scene.add.text(0, 0, "0", {
      fontFamily: UI.fontFamily,
      fontSize: UI.fontSize,
      color: UI.color,
    });
    // right-align the value inside a fixed lane so it never overlaps buttons
    this.valueText
      .setOrigin(1, 0.5)
      .setFixedSize(UI.lane.valueWidth, this.valueText.height);

    this.plusBtn = scene.add
      .image(0, 0, UI.buttons.plus)
      .setScale(UI.buttons.scale)
      .setInteractive({ useHandCursor: true });
    this.minusBtn = scene.add
      .image(0, 0, UI.buttons.minus)
      .setScale(UI.buttons.scale)
      .setInteractive({ useHandCursor: true });

    this.container = scene.add.container(0, 0, [
      this.labelText,
      this.valueText,
      this.plusBtn,
      this.minusBtn,
    ]);

    // horizontal layout within the row
    this.reflowRow();

    this.plusBtn.on("pointerdown", () => {
      if (this._availableRef() <= 0) return;
      this._addedCount += 1;
      this._onPlus();
    });

    this.minusBtn.on("pointerdown", () => {
      if (this._addedCount <= 0) return;
      this._addedCount -= 1;
      this._onMinus();
    });
  }

  // place children with consistent alignment
  private reflowRow() {
    const laneStart = 0;
    const labelX = laneStart;
    const valueRight = labelX + UI.lane.labelWidth + UI.lane.valueWidth;

    this.labelText.x = labelX;
    this.valueText.x = valueRight; // right edge (origin 1,0.5)
    this.plusBtn.x =
      valueRight + UI.lane.xPadding + this.plusBtn.displayWidth / 2;
    this.minusBtn.x =
      this.plusBtn.x +
      this.plusBtn.displayWidth / 2 +
      UI.lane.betweenButtons +
      this.minusBtn.displayWidth / 2;

    // vertically center everything on the row baseline
    const midY = 0;
    this.labelText.y = midY;
    this.valueText.y = midY;
    this.plusBtn.y = midY;
    this.minusBtn.y = midY;
  }

  public setPosition(x: number, y: number) {
    this.container.setPosition(x, y);
  }

  public updateValue(v: number) {
    // show "Name: value" or split label/value lanes; we display name on labelText, value alone on valueText
    this.valueText.setText(String(v));
  }

  public resetAddedCount() {
    this._addedCount = 0;
  }

  public get height(): number {
    // row height = max of children bounds
    return Math.max(
      this.labelText.height,
      this.valueText.height,
      this.plusBtn.displayHeight,
      this.minusBtn.displayHeight
    );
  }
}

// This GUI shows char attribs and allows add/remove when AP are available
export default class CharacterAttributesGUI {
  private availablePointsText!: Phaser.GameObjects.Text;

  private rows: Record<AttributeKey, PlusMinusRow>;
  private char: CharacterAttributes;
  private storage: CharacterStorage;

  constructor(charAttribs: CharacterAttributes) {
    this.char = charAttribs;
    this.storage = new CharacterStorage(); // kept; not used here, but retained if you persist later
    this.rows = {} as any;
  }

  preload(scene: Phaser.Scene) {
    scene.load.image("plus_button", "images/plus_button.png");
    scene.load.image("minus_button", "images/minus_button.png");
  }

  public create(scene: Phaser.Scene, topPosX: number, topPosY: number) {
    const pts = this.char.AvailableAttributePoints;

    this.availablePointsText = scene.add.text(
      topPosX,
      topPosY,
      `Available Points: ${pts}`,
      { fontFamily: UI.fontFamily, fontSize: "32px", color: UI.color }
    );

    // helper to keep rows in sync with shared AP
    const apGetter = () => this.char.AvailableAttributePoints;

    // build rows
    this.rows = {
      str: new PlusMinusRow(
        scene,
        "Strength",
        apGetter,
        () => this.onIncrease("str"),
        () => this.onDecrease("str")
      ),
      vit: new PlusMinusRow(
        scene,
        "Vitality",
        apGetter,
        () => this.onIncrease("vit"),
        () => this.onDecrease("vit")
      ),
      dex: new PlusMinusRow(
        scene,
        "Dexterity",
        apGetter,
        () => this.onIncrease("dex"),
        () => this.onDecrease("dex")
      ),
      int: new PlusMinusRow(
        scene,
        "Intelligence",
        apGetter,
        () => this.onIncrease("int"),
        () => this.onDecrease("int")
      ),
    };

    // initial values
    this.rows.str.updateValue(this.char.Strength);
    this.rows.vit.updateValue(this.char.Vitality);
    this.rows.dex.updateValue(this.char.Dexterity);
    this.rows.int.updateValue(this.char.Intelligence);

    // vertical layout
    const startY = topPosY + this.availablePointsText.height + UI.rowGap;
    const rowsInOrder: Array<[AttributeKey, PlusMinusRow]> = [
      ["str", this.rows.str],
      ["vit", this.rows.vit],
      ["dex", this.rows.dex],
      ["int", this.rows.int],
    ];

    let currentY = startY;
    for (const [, row] of rowsInOrder) {
      // center each row vertically on its own baseline
      row.setPosition(topPosX, currentY + row.height / 2);
      currentY += row.height + UI.rowGap;
    }
  }

  // --- logic: single place that mutates attributes & available pool
  private onIncrease(attr: AttributeKey) {
    if (this.char.AvailableAttributePoints <= 0) return;

    this.char.AvailableAttributePoints -= 1;
    this.storage.SetAvailableAttributePoints(
      this.char.AvailableAttributePoints
    );

    switch (attr) {
      case "str":
        this.char.Strength += 1;
        this.storage.SetStrength(this.char.Strength);
        break;
      case "vit":
        this.char.Vitality += 1;
        this.storage.SetVitality(this.char.Vitality);
        break;
      case "dex":
        this.char.Dexterity += 1;
        this.storage.SetDexterity(this.char.Dexterity);
        break;
      case "int":
        this.char.Intelligence += 1;
        this.storage.SetIntelligence(this.char.Intelligence);
        break;
    }
    this.refreshTexts(attr);
  }

  private onDecrease(attr: AttributeKey) {
    // called only if the row's local "added" guard allowed it
    this.char.AvailableAttributePoints += 1;
    this.storage.SetAvailableAttributePoints(
      this.char.AvailableAttributePoints
    );
    switch (attr) {
      case "str":
        this.char.Strength -= 1;
        this.storage.SetStrength(this.char.Strength);
        break;
      case "vit":
        this.char.Vitality -= 1;
        this.storage.SetVitality(this.char.Vitality);
        break;
      case "dex":
        this.char.Dexterity -= 1;
        this.storage.SetDexterity(this.char.Dexterity);
        break;
      case "int":
        this.char.Intelligence -= 1;
        this.storage.SetIntelligence(this.char.Intelligence);
        break;
    }
    this.refreshTexts(attr);
  }

  private refreshTexts(attrChanged?: AttributeKey) {
    // AP
    this.availablePointsText.setText(
      `Available Points: ${this.char.AvailableAttributePoints}`
    );

    // values
    this.rows.str.updateValue(this.char.Strength);
    this.rows.vit.updateValue(this.char.Vitality);
    this.rows.dex.updateValue(this.char.Dexterity);
    this.rows.int.updateValue(this.char.Intelligence);

    // (optional) if you later support loading/saving, you can reset per-row guards here
    // e.g., when committing points:
    if (!attrChanged) {
      this.rows.str.resetAddedCount();
      this.rows.vit.resetAddedCount();
      this.rows.dex.resetAddedCount();
      this.rows.int.resetAddedCount();
    }
  }
}
